import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import BaseLayout from "src/layouts/BaseLayout"
import Game from "src/views/Game"
import TutorialCoach from "src/components/TutorialCoach"
import TutorialPointer from "src/components/TutorialPointer"
import { selectGameConfig } from "src/store/common"
import { getGames, leaveGame, selectGame, setGame } from "src/store/game"
import {
  markBasicTrainingSeen,
  selectBasicTrainingPending,
  selectRefreshToken,
  selectUserId,
  selectUsername,
  userSlice,
} from "src/store/user"
import { LEARNER_ID, TRAINING_GAME_ID } from "src/tutorial/scenario"
import * as server from "src/tutorial/server"

import "./Tutorial.css"

/* How long a new step waits before it reads an empty modal stack as a clear screen: long enough for
   a sheet the round's reply opens to have opened, short enough that nobody notices the wait. */
const SETTLE_MS = 400

/* Basic training — the route, not a view.

   There is deliberately almost nothing here, and that is the point of the 2026-09-14 rewrite. The
   tutorial used to be its own screens: a city drawn as two building cards, a five-by-three board
   with cells the size of a thumbnail. It taught the rules and none of the game — the owner's ask
   was to "use the same UI in the sense of the map... they need to recognize the UI afterwards", so
   what mounts below is `views/Game`, the real one, with the real map, the real docks and the real
   buttons. A player who finishes this has already used the interface they are about to play in.

   What a route has to do before that view can mount, and it is the same list `views/Replay` works
   through for the same reason — the game view reads the game *out of the store*:

   - **Put a game there.** `tutorial/server.start` seeds it from `opening.json`, which is generated
     from the simulator's own `create_initial_metadata` (`manage.py make_tutorial_opening`), so the
     payload is shaped exactly like a live one.
   - **Answer the buttons.** Every control in that view sends over the game socket, and there is no
     socket. `wsClient.useLocalServer` routes them to `tutorial/server` instead, which replies with
     the same `{action, game}` messages the Django consumer sends.
   - **Be somebody.** The view's hands are live only for a player who holds a seat
     (`canAct = Boolean(ownPlayer) && ...`), so the fixture's seat is re-issued to whoever is
     watching. Signed in, that is their own id; signed out — and this is public on purpose, for the
     same reason the manual is — the store's empty user slice is lent an identity for the duration
     and `reset` on the way out.

   Cleaning up matters more here than in most places: a local server left registered would leave a
   *real* game's buttons talking to a script. Both the teardown below and `stopLocalServer` are load
   bearing. */
const Tutorial = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const gameConfig = useSelector(selectGameConfig)
  const userId = useSelector(selectUserId)
  const username = useSelector(selectUsername)
  const storedGame = useSelector(selectGame)
  const basicTrainingPending = useSelector(selectBasicTrainingPending)
  const [running, setRunning] = useState(false)

  /* The account has now seen basic training — said the moment the lesson opens, not when it is
     finished, so a player who walks out at step two is not walked back in on every sign-in. Only
     for an account the server still has as pending: a replay from the manual has nothing to
     record, and a signed-out learner has no row to record it on. */
  useEffect(() => {
    if (basicTrainingPending) dispatch(markBasicTrainingSeen())
  }, [basicTrainingPending, dispatch])

  // Read at teardown through a ref: a learner who signs in from the header mid-lesson has a real
  // session in the slice by then, and `reset` would throw it away with the borrowed one.
  const signedIn = useRef(false)
  signedIn.current = Boolean(useSelector(selectRefreshToken))

  useEffect(() => {
    if (!gameConfig?.human_config) return undefined

    // Signed out, the user slice is its own initial state, so lending it an id costs nothing and
    // `reset` puts it back exactly. Signed in, it is never touched: the seat is re-issued to the
    // real account instead, by id and by name, which is why the roster shows the player themselves.
    // An id that *is* the learner's was lent by a mount of this view still fading out (the route
    // remounts under `AnimatePresence`), and it is borrowed all the same — read as a real account
    // it would never be given back.
    const borrowed = userId == null || String(userId) === String(LEARNER_ID)
    if (userId == null) dispatch(userSlice.actions.setData({ id: LEARNER_ID, username: t("tutorial.you") }))

    const game = server.start(gameConfig, borrowed ? LEARNER_ID : userId, borrowed ? null : username)
    const run = server.currentRun()
    wsClient.useLocalServer("game", server.handle)
    // The battle step's move is opening the battle view — from the round report or the map's own
    // button, whichever the player finds — so it is heard off the modal stack rather than
    // off any one of those doors.
    const stopWatchingModals = ModalService.onShow((type) => {
      if (type === "BattleModal") server.battleOpened()
    })
    /* A new step waits for the sheets the last one left open (owner, 2026-10-03), and this view is
       the one place that sees the modal stack. Polled, because the stack has no close event — and
       given a moment after the step changes, because a sheet a round brings opens from an
       effect a render or two after the round's reply: checked at once, the screen would read clear
       just before it opened. */
    let unclearSince = null
    const watchForClear = window.setInterval(() => {
      const now = server.snapshot()
      if (!now || now.finished || now.clear) {
        unclearSince = null
        return
      }
      unclearSince ??= Date.now()
      if (Date.now() - unclearSince >= SETTLE_MS && !ModalService.hasOpenModals()) server.markClear()
    }, 100)
    dispatch(setGame([game]))
    setRunning(true)

    return () => {
      stopWatchingModals()
      window.clearInterval(watchForClear)
      /* Only the run this mount started. The route remounts this view under `AnimatePresence` —
         a bare `/tutorial` moved to `/es/tutorial`, say — and the outgoing mount is torn down
         *after* the incoming one has seeded the next lesson. This teardown then nulled the
         server, emptied the store and took the borrowed identity back from under the new
         mount, whose re-seed found no run to re-seed from: "Setting up the field…" for ever, on
         every Spanish registration (2026-09-24). A stop the server refuses means everything
         below is the newer mount's, and it is left alone. */
      if (!server.stop(run)) return
      wsClient.stopLocalServer("game")
      dispatch(leaveGame())
      if (borrowed && !signedIn.current) dispatch(userSlice.actions.reset())
      // A signed-in player may have a real game of their own, and it was displaced by the lesson.
      // Re-read it on the way out, or the route pin has nothing to pin them to until a reload.
      if (!borrowed) dispatch(getGames())
      setRunning(false)
    }
    // Seeded once. Re-running this on every render of a changing config would restart the lesson
    // under the player; the config does not change inside a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(gameConfig?.human_config)])

  /* The store holds one game, and the app's bootstrap puts the player's own in it — so a signed-in
     player whose `getGames` resolves a beat after the lesson starts would watch their real board
     appear under the coach's instructions. While training is running, the tutorial's game is the
     one in the store; anything else that lands there is displaced again.

     And until it is, the game view is not rendered at all. A fresh account is walked in here
     straight out of `registerAndSignIn`, whose bootstrap answers `getGames` with an empty list a
     beat *after* the lesson has seeded the store — and `views/Game` reads `game.players` on its
     first line, so rendering it against that beat is a crash before the effect below can put the
     board back (found registering in the browser, 2026-09-14). */
  const trainingGameInStore = Boolean(storedGame) && String(storedGame.id) === String(TRAINING_GAME_ID)
  useEffect(() => {
    if (!running || trainingGameInStore) return
    const game = server.current()
    if (game) dispatch(setGame([game]))
  }, [running, trainingGameInStore, dispatch])

  if (!running || !trainingGameInStore) {
    return (
      <BaseLayout>
        <div className="tut-booting">{t("tutorial.loading")}</div>
      </BaseLayout>
    )
  }

  /* A fragment, deliberately: `Game` lays itself out through `BaseLayout`'s shell, and wrapping it
     in a div of our own put the map pane inside a box the width of the left dock — the canvas
     drew, at 362px, underneath the dock. The coach is `position: fixed` and needs no parent. */
  return (
    <>
      <Game training />
      <TutorialCoach />
      {/* The hand that points at the next press. Drawn through a portal, so it needs no parent
          either. */}
      <TutorialPointer />
    </>
  )
}

export default Tutorial
